import React, { useEffect, useRef, useState } from "react";
import { SerializedError } from "@reduxjs/toolkit";
import FocusTrap from "focus-trap-react";
import classnames from "classnames";

import { SearchFilterDropdown, SearchField } from "components";
import {
  IAPIState,
  IAutosuggest,
  ISearchResult,
  ISearchSubmitData,
} from "types";

interface SearchFilterProps {
  id: string;
  clearState: () => void;
  onSearch: (data: ISearchSubmitData) => void;
  status: IAPIState;
  error?: SerializedError | null;
  results?: ISearchResult[];
  autosuggest?: IAutosuggest[];
  hasSearched?: boolean;
}

export const SearchFilter: React.FC<SearchFilterProps> = ({
  id,
  onSearch,
  status,
  error,
  results,
  autosuggest,
  hasSearched,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isFocus, setIsFocus] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const keysPressed = useRef<{ [key: string]: boolean }>({});
  const onInputClick = () => {
    if (!hasSearched && !autosuggest?.length) return;
    setDropdownOpen(true);
  };

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      setDropdownOpen(true);
      setIsFocus(true);
    }
  };

  const onInputFocus = () => {
    if (!hasSearched && !autosuggest?.length) return;

    document.addEventListener("keydown", handleEscape);
  };

  const onInputBlur = () => {
    document.removeEventListener("keydown", handleEscape);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      keysPressed.current[event.key] = true;

      if (isDropdownOpen)
        switch (event.key) {
          case "Escape":
            setDropdownOpen(false);
            setIsFocus(false);
            break;
          case "Tab":
            const currentElement = document.activeElement as HTMLElement;
            setDropdownOpen(false);
            setIsFocus(false);
            if (keysPressed.current["Shift"] && keysPressed.current["Tab"]) {
              event.preventDefault();
              if (currentElement) currentElement.blur();
              const focusElement = document.querySelector(
                ".header__menu-button"
              ) as HTMLElement;
              focusElement?.focus();
            } else {
              event.preventDefault();
              if (currentElement) currentElement.blur();
              const nextElement = document.querySelector(
                ".block--content-filter"
              )?.nextElementSibling;
              const nextButton = nextElement?.querySelector(
                "a, button"
              ) as HTMLElement;
              nextButton?.focus();
            }
            break;
        }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      delete keysPressed.current[event.key];
    };

    document.addEventListener("keyup", handleKeyUp);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDropdownOpen]);

  const onSearchFilterFocus = () => {
    setIsFocus(true);
  };

  const onSearchFilterBlur = () => {
    setIsFocus(false);
  };

  useEffect(() => {
    if (hasSearched) {
      setDropdownOpen(true);
      return;
    }

    setDropdownOpen(false);
    setIsFocus(false);
  }, [hasSearched]);

  return (
    <>
      <div
        className={classnames("search-filter__inner", [
          isFocus && "is-focused",
        ])}
        onFocus={onSearchFilterFocus}
        onBlur={onSearchFilterBlur}
      >
        <FocusTrap
          active={isDropdownOpen}
          focusTrapOptions={{
            preventScroll: true,
            clickOutsideDeactivates: true,
            onDeactivate: () => setDropdownOpen(false),
          }}
        >
          <div ref={ref} className="searcbh-filter__form">
            <SearchField
              id={id}
              isSubmitting={status === IAPIState.pending}
              onSubmit={onSearch}
              onChange={onSearch}
              isOpen={isDropdownOpen}
              searchType="onChange"
              onInputFocus={onInputFocus}
              onInputBlur={onInputBlur}
              onInputClick={onInputClick}
              showFocusShadow={false}
            />
            {isDropdownOpen && (results || error) && (
              <div className="search-filter__dropdown" role="listbox">
                <SearchFilterDropdown
                  results={results || []}
                  isLoading={status === IAPIState.pending}
                  error={error}
                />
              </div>
            )}
            {isDropdownOpen && !hasSearched && !!autosuggest?.length && (
              <div className="search-filter__dropdown" role="listbox">
                <SearchFilterDropdown results={autosuggest} />
              </div>
            )}
          </div>
        </FocusTrap>
      </div>
      {isFocus && <div className="search-filter__bg" />}
    </>
  );
};
